import { useState, useEffect } from 'react';
import { FlowNode } from '../../types';
import { JsonTextarea } from './JsonTextarea';

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

const TEXTAREA_CLASS = 'w-full border border-input rounded p-1.5 text-xs font-mono bg-background resize-y';

function inputText(config: Record<string, unknown> | undefined): string {
  const input = config?.input;
  return input && typeof input === 'object' ? JSON.stringify(input, null, 2) : '';
}

export function CodeStepConfig({ node, onUpdate }: Props) {
  const [label, setLabel] = useState((node.config?.label as string) ?? '');
  const [alias, setAlias] = useState((node.config?.alias as string) ?? '');
  const [input, setInput] = useState(() => inputText(node.config));
  const [inputError, setInputError] = useState<string | null>(null);
  const [code, setCode] = useState((node.config?.code as string) ?? '');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLabel((node.config?.label as string) ?? '');
    setAlias((node.config?.alias as string) ?? '');
    setInput(inputText(node.config));
    setInputError(null);
    setCode((node.config?.code as string) ?? '');
  }, [node.id]);

  useEffect(() => {
    let parsedInput: Record<string, unknown> | undefined;
    if (input.trim()) {
      // Invalid JSON is not committed — the last valid config stays persisted.
      let parsed: unknown;
      try {
        parsed = JSON.parse(input);
      } catch {
        setInputError('Invalid JSON');
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setInputError('Top level must be a JSON object');
        return;
      }
      parsedInput = parsed as Record<string, unknown>;
    }
    setInputError(null);

    const next: Record<string, unknown> = {
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
      language: 'javascript',
      ...(parsedInput ? { input: parsedInput } : {}),
      code,
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, alias, input, code]);

  return (
    <div className="flex flex-col gap-2 flex-1 overflow-y-auto">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium" htmlFor="code-label">Label</label>
        <input
          id="code-label"
          type="text"
          className="border border-input rounded p-1.5 text-xs bg-background"
          placeholder="e.g. Build sGTM payload"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium" htmlFor="code-alias">Step alias</label>
        <input
          id="code-alias"
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. build-payload"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium" htmlFor="code-input">Input</label>
        <JsonTextarea
          id="code-input"
          className={`${TEXTAREA_CLASS} min-h-[100px]`}
          placeholder={'{\n  "deal": "{{http-1.body}}",\n  "contacts": "{{http-2.body.results}}"\n}'}
          value={input}
          onChange={setInput}
        />
        {inputError && <p className="text-destructive text-xs mt-0.5">{inputError}</p>}
        <p className="text-xs text-muted-foreground">
          The only data the script can see, available as <span className="font-mono">input</span>.
          String values are templates; a placeholder resolving to an object or array arrives as one.
          Env vars and other steps are visible only when mapped here.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium" htmlFor="code-code">Code</label>
        <JsonTextarea
          id="code-code"
          className={`${TEXTAREA_CLASS} min-h-[200px]`}
          placeholder={'return {\n  ids: input.contacts.map(c => String(c.id))\n};'}
          value={code}
          onChange={setCode}
        />
        <p className="text-xs text-muted-foreground">
          JavaScript function body — end it with <span className="font-mono">return</span>. An object
          becomes the output (<span className="font-mono">{`{{${alias || node.id}.field}}`}</span>),
          anything else lands under <span className="font-mono">result</span>. Sandboxed: no
          network, files, timers or require; 2 s and 32 MB limit. The code is not templated.
          Tab indents; Shift+Tab or Escape then Tab leaves the field.
        </p>
      </div>
    </div>
  );
}

